import type { VitalsBucket } from '@ctw/shared/api/fhir/models/vitals-bucket';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import type { RotatedTableRows } from '@ctw/shared/components/tables/rotated-table';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import { RenderSyncedWithRecordIcon } from '@ctw/shared/content/resource/resource-title-column';
import { useWritebacks } from '@ctw/shared/context/writeback-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import {
  faCircleCheck,
  faCircleExclamation,
  faCirclePlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useVitalModal } from './helpers/use-vital-modal';

export function getVitalRows(
  isWritebackEnabled: boolean,
  vitals: Array<VitalsBucket> = [],
): Array<RotatedTableRows<VitalsBucket>> {
  const rows: Array<RotatedTableRows<VitalsBucket>> = [
    {
      title: 'Vital',
      render: (bucket) => {
        return isWritebackEnabled && !bucket.hasAnyFirstParty ? (
          <DateWithHoverIcon date={bucket.dateDisplay ?? ''} vitalBucket={bucket} />
        ) : (
          (bucket.dateDisplay ?? '')
        );
      },
    },
    {
      title: 'Blood Pressure',
      shortTitle: 'BP',
      render: (bucket) =>
        bucket.bloodPressure &&
        renderVitalWithCheckmark(bucket.bloodPressure.display, bucket.bloodPressure.isFirstParty),
    },
    {
      title: 'Pulse',
      shortTitle: 'HR',
      render: (bucket) =>
        bucket.pulse &&
        renderVitalWithCheckmark(
          bucket.pulse.observation.valueWithoutUnits,
          bucket.pulse.isFirstParty,
        ),
    },
    {
      title: 'Temperature',
      shortTitle: 'Temp',
      render: (bucket) =>
        bucket.temperature &&
        renderVitalWithCheckmark(
          bucket.temperature.observation.valueWithUnits,
          bucket.temperature.isFirstParty,
        ),
    },
    {
      title: 'Respiratory Rate',
      shortTitle: 'RR',
      render: (bucket) =>
        bucket.respiratoryRate &&
        renderVitalWithCheckmark(
          bucket.respiratoryRate.observation.valueWithoutUnits,
          bucket.respiratoryRate.isFirstParty,
        ),
    },
    {
      title: 'Oxygen Saturation',
      shortTitle: 'SpO2',
      render: (bucket) =>
        bucket.oxygenSaturation &&
        renderVitalWithCheckmark(
          bucket.oxygenSaturation.observation.valueWithoutUnits,
          bucket.oxygenSaturation.isFirstParty,
        ),
    },
    {
      title: 'Height/Length',
      shortTitle: 'Ht/Lt',
      render: (bucket) =>
        bucket.height &&
        renderVitalWithCheckmark(
          bucket.height.observation.valueWithUnits,
          bucket.height.isFirstParty,
        ),
    },
    {
      title: 'Weight',
      shortTitle: 'Wt',
      render: (bucket) =>
        bucket.weight &&
        renderVitalWithCheckmark(
          bucket.weight.observation.valueWithUnits,
          bucket.weight.isFirstParty,
        ),
    },
    {
      title: 'BMI',
      render: (bucket) =>
        bucket.bmi &&
        renderVitalWithCheckmark(bucket.bmi.observation.valueWithoutUnits, bucket.bmi.isFirstParty),
    },
  ];

  if (vitals.some((vital) => vital.headCircumference !== undefined)) {
    rows.push({
      title: 'Head Circumference',
      shortTitle: 'HC',
      render: (bucket) =>
        bucket.headCircumference &&
        renderVitalWithCheckmark(
          bucket.headCircumference.observation.valueWithoutUnits,
          bucket.headCircumference.isFirstParty,
        ),
    });
  }
  if (vitals.some((vital) => vital.lmp !== undefined)) {
    rows.push({
      title: 'LMP',
      render: (bucket) =>
        bucket.lmp &&
        renderVitalWithCheckmark(bucket.lmp.observation.valueWithoutUnits, bucket.lmp.isFirstParty),
    });
  }
  if (vitals.some((vital) => vital.pain !== undefined)) {
    rows.push({
      title: 'Pain',
      render: (bucket) =>
        bucket.pain &&
        renderVitalWithCheckmark(
          bucket.pain.observation.valueWithoutUnits,
          bucket.pain.isFirstParty,
        ),
    });
  }

  rows.push(
    {
      title: 'Source',
      shortTitle: 'Source',
      render: (vitalBucket: VitalsBucket) =>
        vitalBucket.binaryIdSourceModel && (
          <ResponsiveSourceDocumentLink model={vitalBucket.binaryIdSourceModel} />
        ),
      greyed: true,
    },
    {
      title: 'Encounter Type',
      shortTitle: 'Enc Type',
      dataIndex: 'typeDisplay',
      greyed: true,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      greyed: true,
    },
  );

  return rows;
}

const DateWithHoverIcon = ({ date, vitalBucket }: { date: string; vitalBucket: VitalsBucket }) => {
  const [isHovering, setIsHovering] = useState(false);
  const openVitalModal = useVitalModal();
  const { writebackStatuses } = useWritebacks();
  const status = writebackStatuses[vitalBucket.key];

  const isLoading = status?.state === 'in-progress';
  const isSuccess = status?.state === 'known-to-builder-new';
  const isError = status?.state === 'error';

  const handleClick = () => {
    if (isLoading || isSuccess) {
      return;
    }
    openVitalModal(vitalBucket);
  };

  return (
    <button
      type="button"
      className={tw`flex items-center gap-2 border-0 bg-transparent p-0 text-left`}
      onMouseEnter={() => !(isLoading || isSuccess) && setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      aria-pressed={isHovering}
      onClick={handleClick}
      disabled={isLoading || isSuccess}
    >
      <span>{date}</span>
      {isLoading && <LoadingSpinner />}
      {isSuccess && (
        <FontAwesomeIcon
          icon={faCircleCheck}
          className={tw`text-success-main`}
          title="Successfully added to chart"
        />
      )}
      {isError && (
        <FontAwesomeIcon
          icon={faCircleExclamation}
          className={tw`cursor-pointer text-error-main`}
          title={`Error adding to chart: ${status.message || 'Unknown error'}. Click to try again.`}
        />
      )}
      {!(isLoading || isSuccess || isError) && isHovering && (
        <FontAwesomeIcon
          icon={faCirclePlus}
          className={tw`cursor-pointer text-primary`}
          title="Add to chart"
        />
      )}
    </button>
  );
};

const renderVitalWithCheckmark = (display: string, isFirstParty: boolean) => (
  <div className={tw`flex justify-between space-x-1.5`}>
    {isFirstParty && (
      <div className={tw`w-[1.5rem] min-w-[1.5rem] max-w-[1.5rem] text-sm`}>
        {RenderSyncedWithRecordIcon(isFirstParty)}
      </div>
    )}
    <span>{display}</span>
  </div>
);
